import { PromoListItem } from "@/core/entities/promotion/PromoListItem";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePromotionDrawer } from "./usePromotionDrawer";
import { usePromotions } from "./usePromotions";

export enum Direction {
  NEXT = "next",
  PREVIOUS = "previous",
}

export function usePromotionNavigation() {
  const {
    promotionDrawerInfo,
    setPromotionDrawerInfo,
    setIsFirstItem,
    setIsLastItem,
  } = usePromotionDrawer();
  const {
    promotions,
    currentPage,
    pageLimit,
    totalCount,
    isLoading,
    isRefreshing,
    setCurrentPage,
    getPromotions,
  } = usePromotions();

  const [direction, setDirection] = useState<Direction>(Direction.NEXT);
  const [isPageChanging, setIsPageChanging] = useState(false);

  const { promoEventId, isPromoDrawerOpen } = promotionDrawerInfo;

  const isListLoading = isLoading || isRefreshing;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / pageLimit)),
    [totalCount, pageLimit]
  );

  const scrollTable = useCallback(() => {
    const tableContainer = document.querySelector(".table-container.sticky");
    const currentPromotionItem = document.querySelector(".current-promotion-item");

    if (currentPromotionItem && tableContainer) {
      const containerRect = tableContainer.getBoundingClientRect();
      const itemRect = currentPromotionItem.getBoundingClientRect();
      const itemTopRelativeToContainer = itemRect.top - containerRect.top + tableContainer.scrollTop;
      const scrollTop = itemTopRelativeToContainer - tableContainer.clientHeight / 2 + itemRect.height / 2;
      tableContainer.scrollTo({ top: scrollTop, behavior: "smooth" });
    }
  }, []);

  const updateDrawerInfo = useCallback(
    (targetItem: PromoListItem) => {
      setPromotionDrawerInfo({
        promoEventId: targetItem.promoEventId,
        promoItem: targetItem,
        isPromoDrawerOpen: true,
        nextPrevEnabled: true,
      });
    },
    [setPromotionDrawerInfo]
  );

  const isFirstPromotion = useCallback(() => {
    if (!promoEventId || promotions.length === 0) return false;
    const currentIndex = promotions.findIndex(p => p.promoEventId === promoEventId);
    return currentPage === 1 && currentIndex === 0;
  }, [promoEventId, promotions, currentPage]);

  const isLastPromotion = useCallback(() => {
    if (!promoEventId || promotions.length === 0) return false;
    const currentIndex = promotions.findIndex(p => p.promoEventId === promoEventId);
    return currentPage === totalPages && currentIndex === promotions.length - 1;
  }, [promoEventId, promotions, currentPage, totalPages]);

  const changePageHandler = useCallback(
    (navDirection: Direction) => {
      if (currentPage === 1 && navDirection === Direction.PREVIOUS) return;
      if (currentPage === totalPages && navDirection === Direction.NEXT) return;
      setIsPageChanging(true);
      setCurrentPage(currentPage + (navDirection === Direction.NEXT ? 1 : -1));
      void getPromotions({ isLoading: false, isRefreshing: true });
    },
    [currentPage, totalPages, setCurrentPage, getPromotions]
  );

  const handlePromotionNavigation = useCallback(
    (navDirection: Direction) => {
      if (!isPromoDrawerOpen || !promoEventId || isListLoading) return;

      setDirection(navDirection);

      const currentIndex = promotions.findIndex(p => p.promoEventId === promoEventId);
      if (currentIndex === -1) return;

      const targetIndex = currentIndex + (navDirection === Direction.NEXT ? 1 : -1);
      const targetItem = promotions[targetIndex];

      if (targetItem) {
        updateDrawerInfo(targetItem);
        scrollTable();
        return;
      }

      changePageHandler(navDirection);
    },
    [
      isPromoDrawerOpen,
      promoEventId,
      isListLoading,
      promotions,
      updateDrawerInfo,
      scrollTable,
      changePageHandler,
    ]
  );

  const handlePageNavigation = useCallback(() => {
    if (promotions.length === 0) return;

    const targetItem =
      direction === Direction.NEXT ? promotions[0] : promotions[promotions.length - 1];

    if (targetItem) {
      updateDrawerInfo(targetItem);
      setIsPageChanging(false);
      scrollTable();
    }
  }, [promotions, direction, updateDrawerInfo, scrollTable]);

  useEffect(() => {
    if (!isPageChanging || isListLoading) return;

    setTimeout(() => {
      handlePageNavigation();
    }, 0);
  }, [isPageChanging, isListLoading, handlePageNavigation]);

  useEffect(() => {
    setIsFirstItem(isFirstPromotion());
    setIsLastItem(isLastPromotion());
  }, [promoEventId, setIsFirstItem, setIsLastItem, isFirstPromotion, isLastPromotion]);

  return { handlePromotionNavigation };
}
